import { inject, NgModule }                 from '@angular/core';
import { RouterModule, Routes, UrlSegment } from '@angular/router';
import { SetupNameComponent }               from './pages/secured/setup-name/setup-name.component';
import { SsoLoginComponent }                from './pages/public/sso-login/sso-login.component';

import { ErrorComponent }                     from './pages/public/error/error.component';
import { ShellComponent }                     from './components/shell/shell.component';
import { AuthGuard }                          from './core/auth/guards/auth.guard';
import { PublicPageGuard }                    from './core/auth/guards/public-page.guard';
import { LoginPageComponent }                 from './pages/public/login/login-page.component';
import { LogoutPageComponent }                from './pages/secured/logout/logout-page.component';
import { RegistrationComponent }              from './pages/public/registration/registration.component';
import { ConfirmRegistrationComponent }       from './pages/public/registration/confirm-registration/confirm-registration.component';
import { ForgotUsernameComponent }            from './pages/public/forgot-credentials/username/forgot-username/forgot-username.component';
import { ForgotPasswordComponent }            from './pages/public/forgot-credentials/password/forgot-password.component';
import { SetupPasswordComponent }             from './pages/public/setup-password/setup-password.component';
import { UsernameRemediationComponent }       from './pages/public/username-remediation/username-remediation.component';
import { SetupSecurityTokenComponent }        from './pages/secured/setup-security-token/setup-security-token.component';
import { EnterVerificationCodeComponent }     from './pages/secured/setup-security-token/enter-verification-code/enter-verification-code.component';
import { SetMobileAppComponent }              from './pages/secured/setup-security-token/set-mobile-app/set-mobile-app.component';
import { SsoSessionEndedComponent }           from './pages/public/sso-logout/sso-session-ended.component';
import { HttpStatusCode }                     from './core/error-handling/http-status-codes';
import { PreInvitationRegistrationComponent } from './pages/secured/registration/pre-agent-registration/pre-invitation-registration.component';
import { FeatureSwitchGuard }                 from './shared/guards/feature-switch.guard';
import { FeatureSwitch }                      from './shared/data/feature-switch';
import { accountDataResolver }                from './api/account-data/account-data.resolver';
import { sessionResolver }                    from './api/session/session.resolver';
import { taxDocumentCustomersResolver }       from './pages/secured/documents/tax-documents/tax-document-customers.resolver';
import { NewPublicShellComponent }            from './pages/public/new-public-shell/new-public-shell.component';
import { PrivacyPolicyComponent }             from './pages/public/privacy-policy/privacy-policy.component';
import { AuthenticationService }              from './core/auth/authentication.service';

export function aspxFiles(urlSegments: UrlSegment[]) {
  const segmentsNo = urlSegments.length;
  return segmentsNo > 0 && urlSegments[segmentsNo - 1].path.endsWith('.aspx') ? {consumed: urlSegments} : null;
}

const appRoutes: Routes = [
  {
    path: '',
    children: [
      {
        path: 'accounts',
        component: ShellComponent,
        loadChildren: () => import('./pages/secured/accounts/accounts.module').then(m => m.AccountsModule),
        canActivate: [AuthGuard],
        data: {title: 'My Accounts'},
        resolve: {
          userSession: sessionResolver,
          taxDocumentCustomers: taxDocumentCustomersResolver
        }
      },
      {
        path: 'account-summary/cdars/:accountId/details',
        component: ShellComponent,
        loadChildren: () => import('./pages/secured/cdars-account-summary/cdars-account-summary.module').then(m => m.CdarsAccountSummaryModule),
        canActivateChild: [AuthGuard],
        resolve: {
          userSession: sessionResolver,
          taxDocumentCustomers: taxDocumentCustomersResolver
        }
      },
      {
        path: 'account-summary/:accountId',
        component: ShellComponent,
        loadChildren: () => import('./pages/secured/account-summary/account-summary.module').then(m => m.AccountSummaryModule),
        canActivateChild: [AuthGuard],
        resolve: {
          userSession: sessionResolver,
          taxDocumentCustomers: taxDocumentCustomersResolver
        }
      },
      {
        path: 'where-can-my-funds-be-placed',
        component: ShellComponent,
        loadChildren: () => import('./pages/secured/where-can-my-funds-be-placed/where-can-my-funds-be-placed.module').then(m => m.WhereCanMyFundsBePlacedModule),
        canActivate: [AuthGuard],
        resolve: {accountsData: accountDataResolver}
      },
      {
        path: 'profile',
        component: ShellComponent,
        loadChildren: () => import('./pages/secured/profile/profile.module').then(m => m.ProfileModule),
        canActivate: [AuthGuard],
        data: {title: 'My Profile'},
        resolve: {userSession: sessionResolver}
      },
      {
        path: 'manage-access',
        component: ShellComponent,
        loadChildren: () => import('./pages/secured/manage-access/manage-access.module').then(m => m.ManageAccessModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'documents',
        component: ShellComponent,
        loadChildren: () => import('./pages/secured/documents/documents.module').then(m => m.DocumentsModule),
        canActivate: [AuthGuard, FeatureSwitchGuard],
        data: {
          featureSwitches: [FeatureSwitch.STATEMENTS, FeatureSwitch.CDARS_STATEMENTS, FeatureSwitch.ICS_TAX_DOCUMENTS, FeatureSwitch.CDARS_TAX_DOCUMENTS],
          mode: 'any'
        },
        resolve: {userSession: sessionResolver}
      },
      {
        path: 'login',
        canActivate: [PublicPageGuard],
        component: LoginPageComponent,
        data: {title: 'Log In'}
      },
      {
        path: 'error',
        component: ErrorComponent,
        data: {title: 'Error'}
      },
      {
        path: 'error/:code',
        component: ErrorComponent,
        data: {title: 'Error'}
      },
      {
        path: 'maintenance',
        component: ErrorComponent,
        data: {title: 'Site Maintenance', code: 503}
      },
      {
        path: 'sso-session-ended',
        component: SsoSessionEndedComponent,
        data: {title: 'Session Ended'}
      },
      {
        path: '',
        component: NewPublicShellComponent,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: '/accounts' // we need an absolute path ('/accounts'), otherwise an empty page will be shown
          },
          {
            path: 'logout',
            component: LogoutPageComponent
          },
          {
            path: '',
            canActivateChild: [PublicPageGuard],
            children: [
              {
                path: 'sso',
                component: SsoLoginComponent,
                data: {title: 'Log In'}
              }
            ]
          },
          {
            path: 'setup/name',
            component: SetupNameComponent,
            canActivate: [AuthGuard],
            data: {title: 'Setup First and Last Name'}
          },
        ]
      },
      {
        path: '',
        component: NewPublicShellComponent,
        children: [
          {
            path: '',
            canActivateChild: [PublicPageGuard],
            children: [
              {
                path: 'register',
                component: RegistrationComponent,
                data: {title: 'Register'}
              },
              {
                path: 'register/invitation',
                component: PreInvitationRegistrationComponent,
                data: {title: 'Register'}
              },
              {
                path: 'registration/confirmation',
                component: ConfirmRegistrationComponent,
                data: {title: 'Confirm registration'}
              },
              {
                path: 'forgot-username',
                component: ForgotUsernameComponent,
                data: {title: 'Forgot Username'}
              },
              {
                path: 'forgot-password',
                component: ForgotPasswordComponent,
                data: {title: 'Forgot Password'}
              },
              {
                path: 'setup-password',
                component: SetupPasswordComponent,
                data: {title: 'Set Password'}
              }
            ]
          },
          {
            path: 'username-remediation',
            component: UsernameRemediationComponent,
            canActivate: [AuthGuard],
            data: {title: 'Confirm Username'}
          },
          {
            path: 'setup-security-token',
            component: SetupSecurityTokenComponent,
            canActivate: [AuthGuard],
            resolve: {
              userMfaOptions: () => inject(AuthenticationService).getUserMfaOptions()
            },
            data: { title: 'Setup Security Token' }
          },
          {
            path: 'set-mobile-app',
            component: SetMobileAppComponent,
            canActivate: [AuthGuard],
            data: {title: 'Text Message Verification'}
          },
          {
            path: 'enter-verification-code',
            component: EnterVerificationCodeComponent,
            canActivate: [AuthGuard],
            data: {title: 'Text Message Verification'}
          }
        ]
      },
      {
        path: '',
        component: NewPublicShellComponent,
        children: [
          {
            path: 'privacy-policy',
            component: PrivacyPolicyComponent,
            data: {title: 'Privacy Policy'}
          }
        ]
      },
      {
        matcher: aspxFiles,
        redirectTo: ''
      },
      {
        path: '**',
        redirectTo: 'error/' + HttpStatusCode.NOT_FOUND
      }
    ]
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(appRoutes, {
      paramsInheritanceStrategy: 'always'
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {  }
